// Plan ▸ Beats (BeatBoard on the Mac, review B16): the acts and beats of a structure, each beat a column with its
// scenes, and a tray of scenes without a beat along the foot. Drag a scene onto a beat to assign it, onto the tray to
// clear it (the Beat field, one change per scene); its menu has Open in Write, Move to Beat and No Beat, and a double
// click opens it. Where the sheet says a beat usually falls, the column says where it starts in the book as it stands
// ("near 10% · starts 23%", terracotta when it's well away once the book has 5,000 words). With no sheet yet, the
// structures to start from. The structure's menu (beside the tabs) edits the beats, swaps the structure or removes it.
// On a phone: the acts and beats as a list, each scene with a menu (BeatList).
import { useMemo, useState } from 'react';
import type { Entity, OpenedProject } from '@writing-suite/core';
import {
  addBeat, BEAT_TEMPLATES, beatBoard, beatsIn, moveBeat, percent, removeAct, removeBeat, sheetFromTemplate,
  type BeatScene, type BeatSheet, type BeatTemplate,
} from '@writing-suite/core/plan';
import { makeID } from '@writing-suite/core';
import { announce } from '../../shell/context.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { planEdit } from '../../store/plan.ts';
import { beatScenesFor, beatSheetFor, parseNear } from '../../store/planModel.ts';
import { cssColor, formatNumber, formatWords } from '../helpers.ts';
import { ConfirmDialog, MenuButton, MenuPopup, type MenuEntry } from '../Menu.tsx';
import { usePhone } from './hooks.ts';

const DRAG_TYPE = 'application/x-writing-suite-fragments';

function useBeats(project: PageProps['project']) {
  const opened = project as OpenedProject;
  const sheet = useMemo(() => (opened.record ? beatSheetFor(opened) : null), [opened]);
  const scenes = useMemo(() => (opened.record ? beatScenesFor(opened, sheet) : []), [opened, sheet]);
  return { sheet, scenes };
}

/** Move to Beat (a heading per act), No Beat, Open in Write. */
function sceneEntries(sheet: BeatSheet, scene: BeatScene, assign: (ids: string[], beat: string | null) => void, open: (id: string) => void, writable: boolean): MenuEntry[] {
  const out: MenuEntry[] = [{ label: 'Open in Write', onSelect: () => open(scene.id) }];
  if (!writable) return out;
  out.push({ kind: 'separator' }, { kind: 'heading', label: 'Move to Beat' });
  for (const act of sheet.acts) {
    const beats = beatsIn(sheet, act.id);
    if (!beats.length) continue;
    out.push({ kind: 'heading', label: act.title });
    for (const b of beats) out.push({ kind: 'radio', label: b.title, checked: b.id === scene.beatID, disabled: b.id === scene.beatID, onSelect: () => assign([scene.id], b.id) });
  }
  if (scene.beatID) out.push({ kind: 'separator' }, { label: 'No Beat', onSelect: () => assign([scene.id], null) });
  return out;
}

export function BeatBoardPage({ project, live }: PageProps) {
  const ws = useWorkspace();
  const phone = usePhone();
  const writable = !!ws?.canWrite && !!live;
  const { sheet, scenes } = useBeats(project);
  const entities = project.entities();
  const [menu, setMenu] = useState<{ x: number; y: number; scene: BeatScene } | null>(null);
  const [target, setTarget] = useState<string | null>(null);

  const assign = (ids: string[], beat: string | null) => {
    if (!live || !ids.length) return;
    planEdit(live, (e) => e.assignToBeat(ids, beat));
    const name = beat ? sheet?.beats.find((b) => b.id === beat)?.title : null;
    announce(name ? `Assigned to ${name}` : 'No beat');
  };
  const open = (fragmentId: string) => ws?.reveal({ fragmentId });
  const onScenes = new Set(scenes.map((s) => s.id));
  const dropped = (e: React.DragEvent): string[] => {
    try {
      return (JSON.parse(e.dataTransfer.getData(DRAG_TYPE) || '[]') as string[]).filter((id) => onScenes.has(id));
    } catch {
      return [];
    }
  };
  const dropZone = (key: string, beat: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!writable || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (target !== key) setTarget(key);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (target === key && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setTarget(null);
    },
    onDrop: (e: React.DragEvent) => {
      setTarget(null);
      if (!writable) return;
      e.preventDefault();
      assign(dropped(e).filter((id) => scenes.find((s) => s.id === id)?.beatID !== beat), beat);
    },
  });

  if (!sheet) {
    return (
      <div className="beats-page">
        <header className="page-head">
          <h1 tabIndex={-1}>Choose a structure</h1>
        </header>
        <p className="lede">
          Beats sit above your scenes: the turns a story passes through. Assign scenes to them by dragging, then Story ▸ Continuity points out beats without
          scenes, scenes without beats, and beats that land far from where the structure expects.
        </p>
        <ul className="beat-templates">
          {BEAT_TEMPLATES.map((t) => {
            const s = sheetFromTemplate(t);
            return (
              <li key={t.id}>
                <button type="button" className="beat-template" data-template={t.id} disabled={!writable} onClick={() => live && planEdit(live, (e) => e.setBeatSheet(s))}>
                  <span className="beat-template-name">{t.name}</span>
                  <span className="beat-template-summary">{t.summary}</span>
                  <span className="beat-template-count">
                    {[s.acts.length === 1 ? '1 act' : `${s.acts.length} acts`, s.beats.length ? `${s.beats.length} beats` : null].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const board = beatBoard(sheet, scenes);
  const sceneCard = (scene: BeatScene) => {
    const pov = scene.pov ? entities.find((e) => e.id === scene.pov) : undefined;
    return (
      <li
        key={scene.id}
        // The contents' filter fades the scenes it leaves out (never hides them: a beat would look empty).
        className={`beat-scene${ws?.filter?.view && !ws.filter.matchesFragment(scene.id) ? ' filtered-out' : ''}`}
        data-fragment-id={scene.id}
        draggable={writable}
        tabIndex={0}
        title={scene.synopsis || scene.title}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(DRAG_TYPE, JSON.stringify([scene.id]));
          e.dataTransfer.setData('text/plain', scene.id);
        }}
        onDoubleClick={() => open(scene.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, scene });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') open(scene.id);
          else if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
            e.preventDefault();
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setMenu({ x: r.left + 16, y: r.bottom, scene });
          }
        }}
      >
        <span className="beat-scene-edge" aria-hidden="true" ref={(el) => { const c = cssColor(pov?.color); if (c) el?.style.setProperty('--edge', c); }} />
        <span className="beat-scene-body">
          <span className="beat-scene-title">{scene.title}</span>
          <span className="beat-scene-meta">{[povName(pov), formatWords(scene.words)].filter(Boolean).join(' · ')}</span>
        </span>
      </li>
    );
  };

  return (
    <div className="beats-page" data-beat-sheet={sheet.name}>
      <header className="page-head">
        <h1 tabIndex={-1}>{sheet.name || 'Beats'}</h1>
        <p className="page-sub">
          {sheet.beats.length === 1 ? '1 beat' : `${sheet.beats.length} beats`} · {formatWords(board.bookWords)}
        </p>
      </header>
      {phone ? (
        <div className="beat-list">
          {board.acts.map(({ act, columns }) => (
            <section key={act.id} className="beat-list-act">
              <h2 className="eyebrow">{act.title}</h2>
              {columns.map((c) => (
                <div key={c.beat.id} className="beat-list-beat" data-beat-id={c.beat.id}>
                  <h3>
                    {c.beat.title}
                    {c.beat.target !== undefined ? <span className="beat-near">near {percent(c.beat.target)}</span> : null}
                  </h3>
                  {c.scenes.length ? (
                    <ul>{c.scenes.map((s) => <li key={s.id} className={ws?.filter?.view && !ws.filter.matchesFragment(s.id) ? 'filtered-out' : undefined}><SceneMenuRow scene={s} entries={sceneEntries(sheet, s, assign, open, writable)} /></li>)}</ul>
                  ) : (
                    <p className="empty-note">No scene yet</p>
                  )}
                </div>
              ))}
            </section>
          ))}
          {board.tray.length ? (
            <section className="beat-list-act">
              <h2 className="eyebrow">Scenes without a beat · {board.tray.length}</h2>
              <ul>{board.tray.map((s) => <li key={s.id}><SceneMenuRow scene={s} entries={sceneEntries(sheet, s, assign, open, writable)} /></li>)}</ul>
            </section>
          ) : null}
        </div>
      ) : (
        <>
          <div className="beat-board" role="region" aria-label="Beat board" tabIndex={0}>
            {board.acts.map(({ act, columns }) => (
              <section key={act.id} className="beat-act" aria-label={act.title}>
                <h2 className="eyebrow">{act.title}</h2>
                <div className="beat-columns">
                  {columns.map((c) => (
                    <section
                      key={c.beat.id}
                      className={`beat-column${target === c.beat.id ? ' drop-target' : ''}`}
                      data-beat-id={c.beat.id}
                      aria-label={`${c.beat.title}, ${c.scenes.length === 1 ? '1 scene' : `${c.scenes.length} scenes`}`}
                      {...dropZone(c.beat.id, c.beat.id)}
                    >
                      <h3 className="beat-title">{c.beat.title}</h3>
                      {c.label ? (
                        <p className={`beat-place${c.off ? ' off' : ''}`} data-beat-place>
                          {c.label}
                        </p>
                      ) : null}
                      {c.beat.guidance ? <p className="beat-guidance">{c.beat.guidance}</p> : null}
                      {c.scenes.length ? <ul className="beat-scenes">{c.scenes.map(sceneCard)}</ul> : <p className="beat-empty">No scene yet. Drop one here.</p>}
                    </section>
                  ))}
                  {columns.length === 0 ? <p className="beat-empty act-empty">No beats in this act. Add some with Edit Beats.</p> : null}
                </div>
              </section>
            ))}
          </div>
          <section className={`beat-tray${target === 'tray' ? ' drop-target' : ''}`} aria-label="Scenes without a beat" data-beat-tray {...dropZone('tray', null)}>
            <h2 className="eyebrow">{board.tray.length ? `Scenes without a beat · ${board.tray.length}` : 'Every scene has a beat'}</h2>
            {board.tray.length ? <ul className="beat-scenes tray-scenes">{board.tray.map(sceneCard)}</ul> : null}
          </section>
        </>
      )}
      {menu ? <MenuPopup label={menu.scene.title} at={menu} entries={sceneEntries(sheet, menu.scene, assign, open, writable)} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}

const povName = (e: Entity | undefined) => e?.name ?? '';

function SceneMenuRow({ scene, entries }: { scene: BeatScene; entries: MenuEntry[] }) {
  return (
    <MenuButton label={scene.title} className="beat-list-scene" entries={entries}>
      <span className="beat-scene-title">{scene.title}</span>
      <span className="card-words">{formatNumber(scene.words)}</span>
    </MenuButton>
  );
}

// MARK: The structure's menu, and the editor

/** Beside the tabs: the structure's name as a menu (Edit Beats…, Use Another Structure, Remove Beat Sheet…). */
export function BeatControls({ project, live }: PageProps) {
  const ws = useWorkspace();
  const writable = !!ws?.canWrite && !!live;
  const { sheet } = useBeats(project);
  const [editing, setEditing] = useState<BeatSheet | null>(null);
  const [replacing, setReplacing] = useState<BeatTemplate | null>(null);
  const [removing, setRemoving] = useState(false);
  if (!sheet) return null;
  const save = (s: BeatSheet | null) => live && planEdit(live, (e) => e.setBeatSheet(s));
  return (
    <div className="plan-controls">
      <MenuButton
        label="Beat sheet"
        className="quiet-button header-menu"
        disabled={!writable}
        entries={[
          { label: 'Edit Beats…', onSelect: () => setEditing(structuredClone(sheet)) },
          { kind: 'separator' },
          { kind: 'heading', label: 'Use Another Structure' },
          ...BEAT_TEMPLATES.map((t): MenuEntry => ({ label: t.name, onSelect: () => setReplacing(t) })),
          { kind: 'separator' },
          { label: 'Remove Beat Sheet…', danger: true, onSelect: () => setRemoving(true) },
        ]}
      >
        {sheet.name || 'Beats'}
      </MenuButton>
      {editing ? (
        <BeatSheetEditor
          sheet={editing}
          onCancel={() => setEditing(null)}
          onSave={(s) => {
            setEditing(null);
            save(s);
          }}
        />
      ) : null}
      {replacing ? (
        <ConfirmDialog
          title={`Use ${replacing.name}?`}
          message={<p>Its beats replace the current ones. Scenes assigned to beats that aren’t in it will have no beat; nothing else changes.</p>}
          confirm={`Use ${replacing.name}`}
          onCancel={() => setReplacing(null)}
          onConfirm={() => {
            const t = replacing;
            setReplacing(null);
            save(sheetFromTemplate(t));
          }}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title="Remove the beat sheet?"
          message={<p>The acts and beats go. Scenes keep their Beat field, so choosing the same structure again brings the assignments back.</p>}
          confirm="Remove"
          onCancel={() => setRemoving(false)}
          onConfirm={() => {
            setRemoving(false);
            save(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Edit Beats (BeatSheetEditor): the structure's name; each act's title and its beats (title, where it usually falls,
 *  what happens there; up, down, remove); Add Beat, Add Act, Remove Act. Rows are keyed by beat id. */
export function BeatSheetEditor({ sheet: initial, onSave, onCancel }: { sheet: BeatSheet; onSave: (s: BeatSheet) => void; onCancel: () => void }) {
  const [sheet, setSheet] = useState(initial);
  const [nears, setNears] = useState<Record<string, string>>(() =>
    Object.fromEntries(initial.beats.map((b) => [b.id, b.target !== undefined ? percent(b.target) : ''])));
  const setBeat = (id: string, patch: Partial<BeatSheet['beats'][number]>) =>
    setSheet((s) => ({ ...s, beats: s.beats.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  const commitNear = (id: string) => {
    const v = parseNear(nears[id] ?? '');
    if (v === undefined) return;
    setSheet((s) => ({
      ...s,
      beats: s.beats.map((b) => {
        if (b.id !== id) return b;
        const { target: _t, ...rest } = b;
        return v === null ? rest : { ...rest, target: v };
      }),
    }));
  };
  const finished = (): BeatSheet => {
    // Near fields not yet left count too.
    const beats = sheet.beats.map((b) => {
      const v = parseNear(nears[b.id] ?? '');
      if (v === undefined) return b;
      const { target: _t, ...rest } = b;
      return v === null ? rest : { ...rest, target: v };
    });
    return { ...sheet, beats };
  };
  return (
    <DialogShell title="Edit Beats" onCancel={onCancel} className="beat-editor">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(finished());
        }}
      >
        <label className="field-label stacked-label" htmlFor="beat-sheet-name">
          Structure
        </label>
        <input id="beat-sheet-name" type="text" value={sheet.name} onChange={(e) => setSheet({ ...sheet, name: e.target.value })} />
        {sheet.acts.map((act) => (
          <fieldset key={act.id} className="beat-editor-act" data-act-id={act.id}>
            <legend className="visually-hidden">{act.title}</legend>
            <div className="beat-editor-act-head">
              <input
                type="text"
                aria-label="Act"
                value={act.title}
                onChange={(e) => setSheet({ ...sheet, acts: sheet.acts.map((a) => (a.id === act.id ? { ...a, title: e.target.value } : a)) })}
              />
              {sheet.acts.length > 1 ? (
                <button type="button" className="link-button danger" onClick={() => setSheet(removeAct(sheet, act.id))}>
                  Remove Act
                </button>
              ) : null}
            </div>
            <ol className="beat-editor-beats">
              {beatsIn(sheet, act.id).map((b) => (
                <li key={b.id} data-beat-id={b.id}>
                  <div className="beat-editor-row">
                    <input type="text" aria-label="Beat" value={b.title} onChange={(e) => setBeat(b.id, { title: e.target.value })} />
                    <input
                      type="text"
                      className="beat-near-input"
                      aria-label={`Near (${b.title})`}
                      title="Where the beat usually falls, as a percentage of the book (leave empty for none)"
                      placeholder="—"
                      inputMode="numeric"
                      value={nears[b.id] ?? ''}
                      onChange={(e) => setNears({ ...nears, [b.id]: e.target.value })}
                      onBlur={() => commitNear(b.id)}
                    />
                    <button type="button" className="icon-button" aria-label={`Move ${b.title} up`} onClick={() => setSheet(moveBeat(sheet, b.id, -1))}>
                      ↑
                    </button>
                    <button type="button" className="icon-button" aria-label={`Move ${b.title} down`} onClick={() => setSheet(moveBeat(sheet, b.id, 1))}>
                      ↓
                    </button>
                    <button type="button" className="icon-button danger" aria-label={`Remove ${b.title}`} onClick={() => setSheet(removeBeat(sheet, b.id))}>
                      −
                    </button>
                  </div>
                  <textarea rows={2} aria-label={`What happens in ${b.title}`} placeholder="What happens here" value={b.guidance} onChange={(e) => setBeat(b.id, { guidance: e.target.value })} />
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="quiet-button"
              onClick={() => {
                const id = makeID();
                setSheet(addBeat(sheet, act.id, id));
                setNears({ ...nears, [id]: '' });
              }}
            >
              Add Beat
            </button>
          </fieldset>
        ))}
        <button type="button" className="quiet-button" onClick={() => setSheet({ ...sheet, acts: [...sheet.acts, { id: makeID(), title: `Act ${sheet.acts.length + 1}` }] })}>
          Add Act
        </button>
        <div className="actions">
          <button type="button" className="button secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="button primary">
            Save
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

/** A modal sheet (`<dialog>`, Escape cancels) for the Plan room's editors. */
export function DialogShell({ title, onCancel, children, className = '' }: { title: string; onCancel: () => void; children: React.ReactNode; className?: string }) {
  const id = useMemo(() => `dialog-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <dialog
      ref={(d) => {
        if (d && !d.open) d.showModal();
      }}
      className={`confirm plan-sheet ${className}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <h2 id={id}>{title}</h2>
      {children}
    </dialog>
  );
}
